import { useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Minus, Plus, X, Pill, Package, Save } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export interface EstoqueModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const medicamentos = [
  { nome: "Omeprazol", apresentacao: "Cápsula 20mg" },
  { nome: "Dolantina", apresentacao: "Solução injetável 50mg/mL" },
  { nome: "Paracetamol", apresentacao: "Comprimido 750mg" },
  { nome: "Plavix", apresentacao: "Comprimido 75mg" },
  { nome: "Atenolol", apresentacao: "Comprimido 25mg" },
  { nome: "Lantus", apresentacao: "Solução injetável 100UI/mL" },
  { nome: "Amoxicilina", apresentacao: "Cápsula 500mg" },
  { nome: "Allegra", apresentacao: "Comprimido 180mg" },
  { nome: "Sinvastatina", apresentacao: "Comprimido 20mg" },
  { nome: "Renitec", apresentacao: "Comprimido 20mg" },
];

export function EstoqueModal({ open, onOpenChange }: EstoqueModalProps) {
  const [quantidades, setQuantidades] = useState<Record<string, string>>({});

  const handleChange = (nome: string, value: string) => {
    setQuantidades((prev) => ({ ...prev, [nome]: value }));
  };

  const adjust = (nome: string, delta: number) => {
    setQuantidades((prev) => {
      const current = parseInt(prev[nome] ?? "0", 10) || 0;
      const next = Math.max(0, current + delta);
      return { ...prev, [nome]: String(next) };
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-full max-w-2xl overflow-hidden rounded-2xl border-none p-0 shadow-2xl [&>button]:hidden">
        <div className="relative overflow-hidden bg-gradient-to-br from-tudo-blue via-tudo-blue to-tudo-blue/80 px-5 py-4 sm:px-6">
          <div className="pointer-events-none absolute -right-8 -top-10 h-32 w-32 rounded-full bg-white/10 blur-xl" />
          <div className="pointer-events-none absolute -bottom-12 -left-6 h-28 w-28 rounded-full bg-tudo-green/20 blur-xl" />
          <div className="relative flex items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/15 shadow-inner ring-1 ring-white/20 backdrop-blur-sm">
                <Package className="h-5 w-5 text-white" />
              </div>
              <div className="min-w-0">
                <DialogTitle className="truncate text-left text-lg font-bold tracking-wide text-white">
                  Estoque
                </DialogTitle>
                <p className="truncate text-xs font-medium text-white/70">
                  Contagem de medicamentos no PDV
                </p>
              </div>
            </div>
            <button
              onClick={() => onOpenChange(false)}
              className="shrink-0 rounded-lg p-1.5 text-white/80 transition-colors hover:bg-white/10 hover:text-white"
              aria-label="Fechar"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="max-h-[70vh] overflow-y-auto bg-muted/30 p-4 sm:p-6">
          <div className="overflow-hidden rounded-lg border border-border bg-card shadow-sm">
            <div className="grid grid-cols-[1fr_auto] items-center gap-3 border-b border-border bg-muted/50 px-4 py-3 sm:gap-6 sm:px-5">
              <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Medicamento / Apresentação
              </span>
              <span className="text-center text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Quantidade
              </span>
            </div>
            <div className="divide-y divide-border">
              {medicamentos.map((med) => (
                <div
                  key={med.nome}
                  className="grid grid-cols-[1fr_auto] items-center gap-3 px-4 py-3 transition-colors hover:bg-muted/30 sm:gap-6 sm:px-5"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-tudo-blue/10">
                      <Pill className="h-4 w-4 text-tudo-blue" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="truncate text-sm font-semibold text-foreground">
                        {med.nome}
                      </h3>
                      <p className="truncate text-xs text-muted-foreground">
                        {med.apresentacao}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center justify-end gap-1.5">
                    <button
                      type="button"
                      onClick={() => adjust(med.nome, -1)}
                      aria-label={`Diminuir quantidade de ${med.nome}`}
                      className="flex h-9 w-9 items-center justify-center rounded-md border border-border bg-background text-tudo-blue transition-colors hover:bg-tudo-blue hover:text-white focus:outline-none focus:ring-2 focus:ring-tudo-blue"
                    >
                      <Minus className="h-4 w-4" />
                    </button>
                    <Input
                      type="number"
                      inputMode="numeric"
                      min={0}
                      placeholder="0"
                      value={quantidades[med.nome] ?? ""}
                      onChange={(e) => handleChange(med.nome, e.target.value)}
                      className="h-9 w-16 px-2 text-center font-semibold focus-visible:ring-tudo-blue"
                    />
                    <button
                      type="button"
                      onClick={() => adjust(med.nome, 1)}
                      aria-label={`Aumentar quantidade de ${med.nome}`}
                      className="flex h-9 w-9 items-center justify-center rounded-md border border-border bg-background text-tudo-green transition-colors hover:bg-tudo-green hover:text-white focus:outline-none focus:ring-2 focus:ring-tudo-green"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-6 flex justify-center">
            <Button
              type="button"
              onClick={() => onOpenChange(false)}
              className="flex items-center justify-center gap-2 bg-gradient-to-r from-tudo-green to-tudo-green/85 px-8 py-5 text-base font-semibold text-white shadow-md shadow-tudo-green/20 transition-all hover:-translate-y-0.5 hover:shadow-lg focus:ring-2 focus:ring-tudo-green focus:ring-offset-2"
            >
              <Save className="h-4 w-4" />
              Salvar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}